import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TableColumn } from '@app/shared';

export interface ActiveFilter {
  columnName: string;
  filterValue: any;
  column: TableColumn;
}

export interface FilterChangeEvent {
  filters: ActiveFilter[];
}

@Component({
  selector: 'app-maestro-filter',
  standalone: false,
  templateUrl: './maestro-filter.component.html',
  styleUrl: './maestro-filter.component.less',
})
export class MaestroFilterComponent implements OnInit {
  @Input() filterableColumns: TableColumn[] = [];
  @Output() filterChange = new EventEmitter<FilterChangeEvent>();
  @Output() filterClear = new EventEmitter<void>();

  // Array de filtros activos
  activeFilters: ActiveFilter[] = [];
  
  // Para el nuevo filtro que se está agregando
  selectedFilterColumn: string | null = null;
  filterValue: any = null;

  // Propiedades calculadas para la vista del nuevo filtro
  currentFilterColumn: TableColumn | undefined = undefined;
  currentFilterType: string | undefined = undefined;
  showFilterInput: boolean = false;
  
  // Columnas disponibles para agregar (excluyendo las ya usadas)
  get availableColumns(): TableColumn[] {
    const usedColumnNames = this.activeFilters.map(f => f.columnName);
    return this.filterableColumns.filter(col => !usedColumnNames.includes(col.name));
  }

  // Verifica si el botón de agregar filtro debe mostrarse
  get canAddFilter(): boolean {
    if (this.filterValue === null || this.filterValue === undefined || this.filterValue === '') {
      return false;
    }
    if (this.currentFilterType === 'multi-select') {
      return Array.isArray(this.filterValue) && this.filterValue.length > 0;
    }
    return true;
  }

  ngOnInit(): void {
    // Inicializar si hay columnas filtrables
    if (this.filterableColumns.length > 0) {
      // Opcional: seleccionar la primera columna por defecto
      // this.onFilterColumnChange(this.filterableColumns[0].name);
    }
  }

  /**
   * Maneja el cambio de columna seleccionada para el nuevo filtro
   */
  onFilterColumnChange(columnName: string | null): void {
    this.selectedFilterColumn = columnName;
    this.filterValue = null;
    this.updateFilterProperties();
  }

  /**
   * Actualiza las propiedades calculadas basadas en la columna seleccionada
   */
  updateFilterProperties(): void {
    if (!this.selectedFilterColumn) {
      this.currentFilterColumn = undefined;
      this.currentFilterType = undefined;
      this.showFilterInput = false;
      return;
    }

    this.currentFilterColumn = this.filterableColumns.find(
      col => col.name === this.selectedFilterColumn
    );

    if (this.currentFilterColumn?.pathData) {
      const type = this.currentFilterColumn.pathData.type;
      // Solo permitir ciertos tipos de filtro
      if (type === 'text' || type === 'number' || type === 'date' || 
          type === 'select' || type === 'avatar-select' || type === 'boolean' || 
          type === 'multi-select') {
        // Tratar avatar-select como select para el filtro
        this.currentFilterType = type === 'avatar-select' ? 'select' : type;
        this.showFilterInput = true;
      } else {
        this.currentFilterType = undefined;
        this.showFilterInput = false;
      }
    } else {
      this.currentFilterType = undefined;
      this.showFilterInput = false;
    }
  }

  /**
   * Maneja el cambio del valor del filtro
   */
  onFilterValueChange(value: any): void {
    this.filterValue = value;
  }

  /**
   * Agrega el filtro actual a la lista de filtros activos
   */
  addFilter(): void {
    if (!this.selectedFilterColumn || !this.currentFilterColumn) return;
    
    // Verificar que el valor no esté vacío (excepto para boolean que puede ser false)
    if (this.filterValue === null || this.filterValue === undefined || 
        (this.filterValue === '' && this.currentFilterType !== 'boolean') ||
        (this.currentFilterType === 'multi-select' && (!Array.isArray(this.filterValue) || this.filterValue.length === 0))) {
      return;
    }

    // Verificar que no exista ya un filtro para esta columna
    if (this.activeFilters.some(f => f.columnName === this.selectedFilterColumn)) {
      return;
    }

    // Agregar el filtro
    this.activeFilters.push({
      columnName: this.selectedFilterColumn!,
      filterValue: this.filterValue,
      column: this.currentFilterColumn
    });

    // Limpiar el selector para agregar otro filtro
    this.selectedFilterColumn = null;
    this.filterValue = null;
    this.updateFilterProperties();
    
    // Emitir el cambio
    this.emitFilterChange();
  }

  /**
   * Elimina un filtro específico
   */
  removeFilter(columnName: string): void {
    this.activeFilters = this.activeFilters.filter(f => f.columnName !== columnName);
    this.emitFilterChange();
  }

  /**
   * Limpia todos los filtros
   */
  clearAllFilters(): void {
    this.activeFilters = [];
    this.selectedFilterColumn = null;
    this.filterValue = null;
    this.updateFilterProperties();
    this.filterClear.emit();
    this.emitFilterChange();
  }

  /**
   * Emite el evento de cambio de filtro
   */
  emitFilterChange(): void {
    this.filterChange.emit({
      filters: [...this.activeFilters]
    });
  }

  /**
   * Obtiene el tipo de filtro para una columna
   */
  getFilterType(column: TableColumn): string | undefined {
    if (!column.pathData) return undefined;
    const type = column.pathData.type;
    if (type === 'text' || type === 'number' || type === 'date' || 
        type === 'select' || type === 'avatar-select' || type === 'boolean' || 
        type === 'multi-select') {
      return type === 'avatar-select' ? 'select' : type;
    }
    return undefined;
  }

  /**
   * Obtiene la etiqueta de una opción para el select
   */
  getOptionLabel(option: any, column: TableColumn): string {
    if (!column.pathData?.nestedPath) {
      return option.name || option.surName || '-';
    }
    const nestedValue = this.getPropertyValue(option, column.pathData.nestedPath);
    return nestedValue || option.name || '-';
  }

  /**
   * Obtiene el valor de una propiedad anidada
   */
  private getPropertyValue(obj: any, path: string): any {
    return path ? path.split('.').reduce((acc, part) => acc?.[part], obj) : '';
  }

  /**
   * Verifica si un valor es un array
   */
  isArray(value: any): boolean {
    return Array.isArray(value);
  }
}

